import type { AccountKey, MailboxInfo } from 'owa-client-types';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isMChatFeatureEnabled } from 'owa-copilot-settings-utils';
import { BizChatSidepaneEnablementCodes } from 'owa-copilot-settings-store/lib/utils/enablementCodes/BizChatSidepaneEnablementCodes';
import type { CopilotEnablementDiagnostics } from 'owa-copilot-settings-store/lib/utils/enablementCodes/CopilotEnablementTypes';
import {
    getBizchatAcquisitionSettings,
    updateBizchatEnabledMutator,
} from 'owa-sidepane-bizchat-store';
import { UserCohort } from 'owa-bizchat-types/lib/UserCohort';
import { lazyGetM365Acquisition } from 'owa-m365-acquisitions/lib/lazyFunction';
import { isCopilotStoreReady } from 'owa-copilot-settings-store';
import getBizChatAppId from 'owa-m365-acquisitions/lib/utils/getBizChatAppId';
import { logCoreUsage } from 'owa-analytics';
import { isBizchatSidepaneDisabledByPolicy } from './isBizchatSidepaneDisabledByPolicy';
import { logBizchatSidepaneEnablementDiagnostics } from './logBizchatSidepaneEnablementDiagnostics';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isVanityDomain } from 'owa-tokenprovider';
import { getHostHub } from 'owa-config/lib/getHostHub';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getCurrentModule } from 'owa-app-module-store';
import { isServiceRequestSupportedForMailbox } from 'owa-service/lib/utils/isServiceRequestSupportedForMailbox';

const isCheckingForBizchatEnabled = new Set<AccountKey>();

/**
 * Checks if Bizchat is enabled for the given mailbox.
 * @param mailboxInfo MailboxInfo for which to check Bizchat enablement
 * @returns Returns the map.get(key) which can be undefined (network call hasn't finished),
 *          or true/false if it has finished.
 */
export function isBizchatSidepaneEnabled(
    mailboxInfo: MailboxInfo = getModuleContextMailboxInfo(),
    shouldIgnoreUserPreferences?: boolean
): boolean | undefined {
    // Return false for PST File, Unified Inbox, and Msg File as Bizchat is not supported
    if (!isServiceRequestSupportedForMailbox(mailboxInfo)) {
        return false;
    }

    // If Copilot or OCPS stores are not ready, we return undefined until they are ready so we can check again.
    if (!isCopilotStoreReady(mailboxInfo)) {
        return undefined;
    }

    // This check is for the user turning OFF Copilot via Settings, and the license check from OCS.
    // It should have precedence over the acquisition check. It also uses a cached value from localStorage.
    const diagnostics: CopilotEnablementDiagnostics = { code: undefined };
    if (
        isMChatFeatureEnabled(
            mailboxInfo,
            true /* skipLanguageCheck */,
            shouldIgnoreUserPreferences,
            diagnostics
        ) === false
    ) {
        logBizchatSidepaneEnablementDiagnostics(mailboxInfo, diagnostics);
        return false;
    }

    // Check for OCPS policies that might disable Bizchat sidepane and early return if it's disabled.
    if (isBizchatSidepaneDisabledByPolicy(mailboxInfo, diagnostics)) {
        logBizchatSidepaneEnablementDiagnostics(mailboxInfo, diagnostics);
        return false;
    }

    if (!isGlobalFeatureEnabled('mon-copilot-bizchat-vanity-killswitch') && isVanityDomain()) {
        diagnostics.code = BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_DISABLED_VANITY_DOMAIN;
        logBizchatSidepaneEnablementDiagnostics(mailboxInfo, diagnostics);
        return false;
    }

    // Check if the mailbox is enabled for Bizchat in the Bizchat shared store
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const isBizchatSidepaneEnabledForMailbox = getBizchatAcquisitionSettings(accountKey);
    if (isBizchatSidepaneEnabledForMailbox === undefined) {
        // If the value is undefined, it means we need to check if Bizchat is enabled for this mailbox

        if (isCheckingForBizchatEnabled.has(accountKey)) {
            // If we are already checking for this mailbox, return undefined to avoid multiple accesses
            // to the Apollo cache since this can cause overhead and multiple components rely on this call
            return undefined;
        }
        isCheckingForBizchatEnabled.add(accountKey);
        // If the value is not set, we need to fetch it from M365 Acquisitions
        lazyGetM365Acquisition
            .importAndExecute(
                getBizChatAppId(mailboxInfo),
                'cache-first',
                mailboxInfo,
                true /* shouldIgnoreUserPreferences */
            )
            .then(acquisition => {
                const hasAcquisition = acquisition.acquisition !== undefined;

                logCoreUsage('OutlookCopilotChat_HasBizChatAcquisition', mailboxInfo, {
                    hasAcquisition,
                    fromCache: !acquisition.recentlyInstalled,
                });
                // Update the store with the acquisition result and reset the checking flag
                updateBizchatEnabledMutator(
                    accountKey,
                    hasAcquisition,
                    acquisition?.acquisition?.titleDefinition?.eligibility ?? undefined
                );
                isCheckingForBizchatEnabled.delete(accountKey);
            });
        if (!isGlobalFeatureEnabled('mon-copilot-bizchat-eligibility-killswitch')) {
            // Return undefined while the acquisition check is in progress so we don't log multiple times
            return undefined;
        }
    }

    const hasM365Acquisition = isBizchatSidepaneEnabledForMailbox?.hasAcquisition;
    const isCopilotEligible = isBizchatSidepaneEnabledForMailbox?.isCopilotEligible;
    const cohort = isBizchatSidepaneEnabledForMailbox?.userCohort;
    if (hasM365Acquisition !== undefined) {
        // If we have a definitive answer for hasM365Acquisition, log the result
        if (hasM365Acquisition) {
            if (isCopilotEligible === false) {
                diagnostics.code =
                    BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_COPILOT_INELIGIBLE;
            } else {
                diagnostics.code = BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_ENABLED;
            }
        } else {
            diagnostics.code =
                BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_ACQUISITION_NOT_FOUND;
        }
        logBizchatSidepaneEnablementDiagnostics(mailboxInfo, diagnostics, cohort);
    }

    // Early return for Starter users in Teams Calendar
    if (
        !isGlobalFeatureEnabled('cal-teams-copilot-starter-license-check-ripcord') &&
        getHostHub() === 'teamshub' &&
        getCurrentModule() === 'Calendar' &&
        !isConsumer() &&
        cohort === UserCohort.BCWAF
    ) {
        return false;
    }

    if (isGlobalFeatureEnabled('mon-copilot-bizchat-eligibility-killswitch')) {
        return hasM365Acquisition;
    } else {
        return hasM365Acquisition && isCopilotEligible !== false;
    }
}
